import { t } from '@trimble/i18n';
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  JSXElement,
  Match,
  Show,
  Switch,
} from 'solid-js';
import { Key } from '@solid-primitives/keyed';

import { BtnClasses, Icon, IconButton } from '@tcweb/connect-ui';
import {
  Measurement,
  MeasurementsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  PanelLayoutWithHeaderToggle,
  PanelHeaderSearchInput,
} from '@trimble/ui-components/solidjs';
import { OptionalTierLock } from '@trimble/tiering';
import {
  TieringBehaviors,
  TieringConfig,
  TieringFeatures,
} from '@trimble/types';
import { RequestAccessBanner } from './RequestAccessBanner';

export interface MeasurementFilter {
  searchText?: string;

  customFilter?(measurement: Measurement): boolean;
}

export const createMeasurementFilter = {
  bySearchText: (searchText: string): MeasurementFilter => ({
    searchText,
  }),

  custom: (
    filterFn: (measurement: Measurement) => boolean
  ): MeasurementFilter => ({
    customFilter: filterFn,
  }),

  combine: (
    searchText?: string,
    customFilter?: (measurement: Measurement) => boolean
  ): MeasurementFilter => ({
    searchText,
    customFilter,
  }),
};

export interface MeasurementsListPanelProps {
  measurementsProvider: MeasurementsCapabilityProvider;
  initialFilter?: MeasurementFilter;
  onMeasurementClick(measurementId: string): void;
  connectPermissionError?: string;
  onRequestAccess?(): Promise<void>;
  options?: { enableTiering?: boolean };
  disableDelete?: boolean;
  onFilterSet?(filter: MeasurementFilter): void;
  disabledReason?: string;
  onVisibilityToggle?(measurement: Measurement): void;
  isMeasurementBusy?(measurementId: string): boolean;
}

export const MeasurementsListPanel: Component<MeasurementsListPanelProps> = (
  props
) => {
  const tieringOptions = createMemo((): TieringConfig | undefined =>
    props.options?.enableTiering
      ? {
          featureName: TieringFeatures.Measurements,
          showBadge: false,
          disabledBehavior: TieringBehaviors.Restricted,
        }
      : undefined
  );

  const [isSearchOpen, setIsSearchOpen] = createSignal<boolean>(false);

  const [activeMeasurementsFilter, setActiveMeasurementsFilter] = createSignal<
    MeasurementFilter | undefined
  >(props.initialFilter);

  createEffect(() => {
    if (props.initialFilter) setActiveMeasurementsFilter(props.initialFilter);
  });

  const handleDelete = (measurementId: string): void => {
    props.measurementsProvider.removeMeasurement(measurementId);
  };

  const filteredMeasurements = createMemo(() => {
    const measurements = props.measurementsProvider.items();

    const filter = activeMeasurementsFilter();

    if (!filter) {
      return measurements;
    }

    const filtered = measurements.filter((measurement) => {
      if (!!filter && filter.searchText?.length) {
        const searchLower = filter.searchText.toLowerCase();
        const labelMatch =
          measurement.label?.toLowerCase().includes(searchLower) ?? false;

        if (!labelMatch) {
          return false;
        }
      }

      return !filter.customFilter || filter.customFilter(measurement);
    });

    props.onFilterSet?.(filter);
    return filtered;
  });

  const setSearchText = (text: string): void => {
    if (props.initialFilter) {
      setActiveMeasurementsFilter(
        createMeasurementFilter.combine(text, props.initialFilter.customFilter)
      );
    } else {
      setActiveMeasurementsFilter(createMeasurementFilter.bySearchText(text));
    }
  };

  const measurementIconMap: Record<Measurement['type'], string> = {
    polyline: 'polygon_line_tool',
    line: 'polygon_line_tool',
    angle: 'tc-icon-measure-angle',
    point: 'location',
    alignment: 'tc-icon-path-navigatio',
    volume: 'svg-compute-volume',
    passCount: 'svg-compute-volume',
    area: 'tc-icon-measure-area',
  };

  const getMeasurementItemTestId = (measurement: Measurement): string => {
    return `measurement-${measurement.type}-item`;
  };

  const handleShowOrHide = (measurement: Measurement): void => {
    props.measurementsProvider.toggleMeasurementVisibility(measurement);
    props.onVisibilityToggle?.(measurement);
  };

  const ToggleButton = (buttonProps: {
    measurement: Measurement;
  }): JSXElement => {
    return (
      <span
        data-testid="measurement-toggle-button"
        class="cursor-pointer inline-flex"
      >
        <modus-button
          button-style="borderless"
          color={`${props.measurementsProvider.selectedItemId() === buttonProps.measurement.id ? 'primary' : 'secondary'}`}
          icon-only={
            buttonProps.measurement.hidden ? 'visibility_off' : 'visibility_on'
          }
          size="small"
          onClick={() => {
            handleShowOrHide(buttonProps.measurement);
          }}
        />
      </span>
    );
  };

  const InfoButton = (buttonProps: {
    measurement: Measurement;
    tieringOptions?: TieringConfig;
  }): JSXElement => {
    return (
      <OptionalTierLock options={buttonProps.tieringOptions}>
        <span
          data-testid="measurement-info-button"
          class="cursor-pointer inline-flex"
        >
          <modus-button
            button-style="borderless"
            color={`${props.measurementsProvider.selectedItemId() === buttonProps.measurement.id ? 'primary' : 'secondary'}`}
            icon-only="info"
            size="small"
            onClick={() => {
              props.onMeasurementClick(buttonProps.measurement.id);
            }}
          />
        </span>
      </OptionalTierLock>
    );
  };

  const DeleteButton = (buttonProps: {
    measurement: Measurement;
    tieringOptions?: TieringConfig;
  }): JSXElement => {
    return (
      <OptionalTierLock options={buttonProps.tieringOptions}>
        <modus-wc-tooltip
          content={props.disabledReason ?? ''}
          position="bottom"
          disabled={!props.disableDelete}
        >
          <span
            data-testid="measurement-delete-button"
            class="cursor-pointer inline-flex"
          >
            <modus-button
              button-style="borderless"
              color={`${props.measurementsProvider.selectedItemId() === buttonProps.measurement.id ? 'primary' : 'secondary'}`}
              icon-only="delete"
              size="small"
              onClick={() => {
                handleDelete(buttonProps.measurement.id);
              }}
              disabled={props.disableDelete}
              class={`${props.disableDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </span>
        </modus-wc-tooltip>
      </OptionalTierLock>
    );
  };

  return (
    <div data-testid="measurements-list-panel" class="contents">
      <PanelLayoutWithHeaderToggle
        panelTitle={t('measurements.measurements')}
        rightActions={
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-search"
            size="icon-medium"
            clicked={() => setIsSearchOpen(true)}
          />
        }
        toggleContent={
          <PanelHeaderSearchInput
            filterString={() => activeMeasurementsFilter()?.searchText ?? ''}
            setFilterString={setSearchText}
            setIsSearchOpen={setIsSearchOpen}
            placeHolderText={t('measurements.searchText')}
          />
        }
        showToggleContent={isSearchOpen()}
        content={
          <>
            <Show when={props.connectPermissionError}>
              <RequestAccessBanner
                errorMessage={props.connectPermissionError!}
                onRequestAccess={props.onRequestAccess}
              />
            </Show>
            <Show
              when={filteredMeasurements().length > 0}
              fallback={
                <div class="flex flex-col items-center justify-center text-trimble-gray-425 h-full mx-2 py-8">
                  <div class="mb-2">
                    <Icon icon="tc-icon-measure" size="icon-large" />
                  </div>
                  <div class="text-center text-sm font-normal break-normal">
                    <Show
                      when={activeMeasurementsFilter()?.searchText}
                      fallback={t('measurements.noMeasurements')}
                    >
                      {t('measurements.noResults', {
                        term: activeMeasurementsFilter()?.searchText,
                      })}
                    </Show>
                  </div>
                </div>
              }
            >
              <div class="flex flex-col w-full my-1">
                <Key
                  each={filteredMeasurements()}
                  by={(measurement) => measurement.id}
                >
                  {(measurement) => (
                    <div
                      data-testid={getMeasurementItemTestId(measurement())}
                      class={`flex items-center py-1 px-2 my-1 mx-2 rounded-lg cursor-pointer ${
                        props.measurementsProvider.selectedItemId() ===
                        measurement().id
                          ? 'bg-blue-50'
                          : 'hover:bg-trimble-gray-150'
                      }`}
                    >
                      <Icon
                        icon={measurementIconMap[measurement().type]}
                        size="icon-medium"
                        class="mr-2 my-1 text-gray-700 flex-shrink-0"
                      />
                      <div class="flex-1 min-w-0">
                        <modus-wc-tooltip
                          class="flex-1 min-w-0"
                          content={measurement().label ?? ''}
                          position="left"
                          disabled={!measurement().label}
                        >
                          <p
                            class={`text-xs p-1 pl-0.5 truncate m-0 ${
                              props.measurementsProvider.selectedItemId() ===
                              measurement().id
                                ? 'text-blue-650'
                                : 'text-trimble-gray-550'
                            }`}
                          >
                            {measurement().label}
                          </p>
                        </modus-wc-tooltip>
                        <div class="text-xs pb-0.5 pl-0.5 italic">
                          <Switch>
                            <Match
                              when={
                                measurement().savedStatus ===
                                'saving_in_progress'
                              }
                            >
                              <p>
                                {t(
                                  'measurements.measurementSaveStatus.savingInProgress'
                                )}
                              </p>
                            </Match>
                            <Match
                              when={measurement().savedStatus === 'save_failed'}
                            >
                              <p class="text-red">
                                {t(
                                  'measurements.measurementSaveStatus.failedToSave'
                                )}
                              </p>
                            </Match>
                          </Switch>
                        </div>
                      </div>
                      <div class="mt-1 flex items-center gap-2 flex-shrink-0">
                        <Show
                          when={props.isMeasurementBusy?.(measurement().id)}
                        >
                          <modus-wc-loader
                            data-testid="measurement-busy-indicator"
                            aria-label={t('general.loading')}
                            color="primary"
                            size="xs"
                            variant="spinner"
                          />
                        </Show>
                        {/* Don't conditionally set classes on icon directly because it overrides
                        'hydrated' class and that can hide icons (depending or global stylesheets) */}
                        <ToggleButton measurement={measurement()} />
                        <InfoButton
                          measurement={measurement()}
                          tieringOptions={tieringOptions()}
                        />
                        <DeleteButton
                          measurement={measurement()}
                          tieringOptions={tieringOptions()}
                        />
                      </div>
                    </div>
                  )}
                </Key>
              </div>
            </Show>
          </>
        }
      />
    </div>
  );
};
